import { Dialog } from "@/design-system/Dialog/Dialog";
import { hasImportedBeforeSelector } from "@/redux/user/userSlice";
import { useTranslation } from "@repo/shared/i18n";
import { useSelector } from "react-redux";

import { IMPORT_DIALOG_CTA_LABEL, emptyClubDialogCopy } from "./emptyClubCopy";
import { useStartClubImport } from "./useStartClubImport";

/** Set by useEmptyClubGuard from the surface that raised the dialog. */
export type EmptyClubData = {
  section?: string;
  subsection?: string;
};

type Props = {
  data?: EmptyClubData;
  onClose: () => void;
};

/**
 * The one empty-club dialog, mounted by ModalControllerV2 and opened from
 * anywhere with `showDialog({ modalType: "empty-club" })`. It only ever
 * appears in response to an action that needs club players: choosing a club
 * scope, solving an SBC with the club, updating recommendations.
 *
 * Returning users start the import right here, in the same modal slot: the
 * prompt fires on pages that already own an import button (My Club, the SBC
 * sidebar), so routing them somewhere to press another button would be a
 * detour, or on My Club a no-op.
 */
export const EmptyClubDialog = ({ data, onClose }: Props) => {
  const { t } = useTranslation(["myClub", "common"]);
  const hasImportedBefore = useSelector(hasImportedBeforeSelector);
  // useEmptyClubGuard already recorded the intent when it raised this dialog;
  // repeating it here is harmless (same values, same trigger) and keeps the
  // button honest if the dialog is ever opened some other way.
  const startClubImport = useStartClubImport({
    section: data?.section ?? "My Club",
    subsection: data?.subsection,
  });

  const { title, body } = emptyClubDialogCopy(hasImportedBefore);

  return (
    <Dialog
      isOpen
      onClose={onClose}
      title={t(title)}
      posLabel={t(IMPORT_DIALOG_CTA_LABEL)}
      /*
        No local import handling left. startClubImport either routes, or raises
        the overlay through the same modal slot this dialog occupies — so the
        dialog is replaced rather than needing to stay mounted behind it, which
        is what the old isImporting ref was guarding.
      */
      onPositive={startClubImport}
    >
      {t(body)}
    </Dialog>
  );
};
